import { LeadDetailTab, LineOfBusinessName } from '@breathelife/types';
import _ from 'lodash';
import { ReactElement, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabType } from '../../../../Components/Tabs/Tabs';
import { useCarrierContext } from '../../../../Hooks';
import { DetailViewType } from '../../../../Models/Layout';

export type DetailTabsProps = {
  selectedTab: number | undefined;
  onSelectedTabChange: (event: SyntheticEvent, value: number) => void;
  type: DetailViewType;
  hideNotes?: boolean;
  hideNeedsAnalysis?: boolean;
  hideTransactionFlow?: boolean;
  disabled?: boolean;
  applicationLineOfBusiness?: LineOfBusinessName;
};

export function LeadDetailTabs(props: DetailTabsProps): ReactElement {
  const { t } = useTranslation();
  const { enabledTabs, features } = useCarrierContext();

  const isUWRequirementsEnabled =
    features.underwritingRequirements.enabled &&
    props?.applicationLineOfBusiness &&
    features.underwritingRequirements.allowedLinesOfBusiness.includes(props.applicationLineOfBusiness);

  const personalInfoTab = enabledTabs[LeadDetailTab.personalInfo];
  const needsAnalysisTab = enabledTabs[LeadDetailTab.needsAnalysis];
  const notesTab = enabledTabs[LeadDetailTab.notes];
  const historyTab = enabledTabs[LeadDetailTab.history];
  const applicationsTab = enabledTabs[LeadDetailTab.applications];
  const underwritingRequirementsTab = isUWRequirementsEnabled
    ? enabledTabs[LeadDetailTab.underwritingRequirements]
    : undefined;
  const isCreateLeadView = props.type === DetailViewType.create;

  const tabs: TabType[] = [];

  if (personalInfoTab)
    tabs.push({ label: personalInfoTab.title || t('personalInformation'), disabled: props.disabled });

  if (!isCreateLeadView) {
    if (needsAnalysisTab) {
      tabs.push({
        label:
          needsAnalysisTab.title ?? (features.needsAnalysis.enabled && features.needsAnalysis.optional)
            ? t('questionnaire')
            : t('needsAnalysis'),
        hidden: props.hideNeedsAnalysis,
        disabled: props.type === DetailViewType.create || props.disabled,
      });
    }
    if (applicationsTab) {
      tabs.push({
        label: applicationsTab.title ?? t('applications'),
        disabled: props.type === DetailViewType.create || props.disabled,
      });
    }
    if (notesTab) {
      tabs.push({
        label: notesTab.title ?? t('notes'),
        hidden: props.hideNotes,
        disabled: props.type === DetailViewType.create || props.disabled,
      });
    }
    if (historyTab) {
      tabs.push({
        label: historyTab.title ?? t('history'),
        disabled: props.type === DetailViewType.create || props.disabled,
      });
    }
    if (underwritingRequirementsTab) {
      tabs.push({
        label: underwritingRequirementsTab.title ?? t('underwritingRequirements'),
        disabled: props.type === DetailViewType.create || props.disabled,
      });
    }
  }

  return (
    <Tabs
      value={!_.isNil(props.selectedTab) ? props.selectedTab : false}
      onChange={props.onSelectedTabChange}
      tabs={tabs}
    />
  );
}
